import {
  defaultFooterTearCopy,
  getItemRarity,
  resolveRarityColor,
  resolveThemeSpec,
} from "@loot-os/api-contract";
import { rarityTier } from "@loot-os/design-system/domain/rarity";
import type { FooterTearContent } from "@loot-os/design-system/templates/shared/content";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { boxesQueryOptions } from "./queries.js";
import { useTenant } from "./tenant.js";
import { useTerminology } from "./terminology.js";

/**
 * The footer's pack tear, while the store's theme asks for it (`layout.footer: "tear"`): the cards
 * it can reveal are the store's boxes' top items (GET /v1/boxes), each in its tier against its
 * box's price, worded by `defaultFooterTearCopy`. Left out with the theme's plain footer, and
 * until a box with a top item has loaded.
 */
export function useFooterTear(): FooterTearContent | undefined {
  const { config } = useTenant();
  const terms = useTerminology();
  const on = resolveThemeSpec(config.theme).layout.footer === "tear";
  const boxes = useQuery({ ...boxesQueryOptions(), enabled: on });
  const rarity = config.theme.rarity;
  // One object until the boxes, the theme's rarity names or the words change, so the tear (in the
  // chrome, which renders on every navigation and balance change) sees the same pulls.
  return useMemo(() => {
    if (!on) return undefined;
    const pulls = (boxes.data ?? []).flatMap((box) => {
      if (!box.topItem) return [];
      const tier = getItemRarity(box.topItem.value, box.priceCents);
      return [
        {
          id: box.id,
          name: box.topItem.name,
          image: box.topItem.imageUrl,
          cents: box.topItem.value,
          tier: rarityTier(tier),
          tierLabel: resolveRarityColor(tier, rarity).label,
        },
      ];
    });
    if (pulls.length === 0) return undefined;
    const copy = defaultFooterTearCopy(terms);
    return {
      pulls,
      words: {
        caption: copy.caption,
        hint: copy.hint,
        hintTouch: copy.hintTouch,
        strip: copy.strip,
        reset: copy.reset,
      },
      action: { label: copy.action, href: "/boxes" },
    };
  }, [on, boxes.data, rarity, terms]);
}
